import { Box, Grid2 as Grid, Stack } from "@mui/material"
import useModalTrigger from "magik-react-hooks/useModalTrigger"
import { useParams, useNavigate } from "react-router-dom"
import DialogForm from "../../components/DialogForm"
import { useSnackbarMessage } from "../../hooks/snackbar"
import { DataField, DataRow } from "../../components/DataField"
import { SectionTitle } from "../../components/SectionTitle"
import { useTranslation } from "react-i18next"
import RecordInfo from "../../components/RecordInfo"
import DeleteBar from "../../components/DeleteBar"
import DialogConfirm from "../../components/DialogConfirm"
import {
  useFileDigitale,
  useFileDigitaleDelete,
  useFileDigitaleHistory,
  useFileDigitaleUpdate,
} from "../../hooks/filesDigitali"
import FileDigitaleForm from "../../components/forms/FileDigitaleForm"
import FileDigitaleFileForm from "../../components/forms/FileDigitaleFileForm"
import { useDesiredHeight } from "../../hooks/measuring"
import { HistoryBag, FileDigitale } from "../../types"
import { omit } from "../../utils/utils"
import useUserCanWriteAndDelete from "../../hooks/permissions"
import { useIsMobile } from "../../hooks/mobile"

export default function FileDigitaleDettaglio() {
  const { fileDigitaleId: id } = useParams()
  const { t, i18n } = useTranslation()
  const lang = i18n.language
  const { data: fileDigitale, refetch } = useFileDigitale(parseInt(id as string))
  const userCanWriteAndDelete = useUserCanWriteAndDelete()
  const isMobile = useIsMobile()

  const [updateModalState, updateModalActions] = useModalTrigger()
  const [updateFileModalState, updateFileModalActions] = useModalTrigger()
  const [deleteModalState, deleteModalActions] = useModalTrigger()

  const { mutateAsync: update } = useFileDigitaleUpdate()
  const { mutateAsync: remove } = useFileDigitaleDelete()

  const showMessage = useSnackbarMessage()
  const navigate = useNavigate()

  const historyBag = useFileDigitaleHistory(id as string)
  const { ref, desiredHeight } = useDesiredHeight(24)

  return (
    <>
      <Grid
        container
        spacing={2}
        sx={{
          overflow: "auto",
          p: 3,
          pt: 2,
        }}
      >
        <Grid
          size={{
            xs: 12,
            xl: 6,
          }}
        >
          <Stack
            flexGrow={1}
            sx={{
              height: "fit-content",
              position: "relative",
              bgcolor: "white",
              boxShadow: 1,
            }}
          >
            <SectionTitle
              first
              title={t("sections.identificazione")}
              onEdit={() => {
                updateModalActions.open()
              }}
            />
            <Box px={3} pb={2}>
              <DataRow>
                <DataField label={t("fileDigitale.filename")} value={fileDigitale?.filename} size={6} />
                <DataField
                  label={t("fileDigitale.estensione")}
                  value={fileDigitale?.estensione_data?.termine}
                  size={6}
                />
              </DataRow>
              <DataRow>
                <DataField
                  label={t("general.supporto")}
                  value={fileDigitale?.segnatura_supporto}
                  size={6}
                  link={`/supporti/${fileDigitale?.supporto}`}
                />
                <DataField
                  label={t("general.fondo")}
                  value={fileDigitale?.segnatura_fondo}
                  size={6}
                  link={`/supporti/${fileDigitale?.fondo}`}
                />
              </DataRow>

              <DataRow>
                <DataField label={t("fileDigitale.posizione_file")} value={fileDigitale?.posizione_file} size={12} />
              </DataRow>
              <DataRow>
                <DataField label={t("fileDigitale.posizione_lto")} value={fileDigitale?.posizione_lto} size={12} />
              </DataRow>

              <DataRow>
                <DataField label={t("fileDigitale.titolo")} value={fileDigitale?.titolo} size={12} />
              </DataRow>

              <DataRow>
                <DataField label={t("fileDigitale.tipo_file")} value={fileDigitale?.tipo_file_data?.termine} size={6} />
              </DataRow>

              <DataRow>
                <DataField
                  label={t("fileDigitale.note_tecniche")}
                  value={lang === "it" ? fileDigitale?.note_tecniche_it : fileDigitale?.note_tecniche_en}
                  size={12}
                />
              </DataRow>

              <DataRow last>
                <DataField
                  label={t("fileDigitale.note")}
                  value={lang === "it" ? fileDigitale?.note_it : fileDigitale?.note_en}
                  size={12}
                />
              </DataRow>
            </Box>
          </Stack>
        </Grid>

        <Grid
          size={{
            xs: 12,
            xl: 6,
          }}
        >
          <Stack
            flexGrow={1}
            sx={{
              height: "100%",
              bgcolor: "white",
              borderRadius: 1,
              boxShadow: 1,
            }}
          >
            <SectionTitle
              first
              title={t("sections.caratteteristiche_file")}
              onEdit={() => {
                updateFileModalActions.open()
              }}
            />
            <Box px={3} py={2}>
              {fileDigitale?.file && (
                <video
                  controls
                  controlsList="nodownload"
                  onContextMenu={(e) => e.preventDefault()}
                  src={fileDigitale?.file}
                  style={{ width: "100%" }}
                />
              )}

              <DataRow>
                <DataField label={t("general.formato")} value={fileDigitale?.formato_file_data?.termine} size={6} />
                <DataField label={t("fileDigitale.dimensione_file")} value={fileDigitale?.dimensione_file} size={6} />
              </DataRow>
              <DataRow>
                <DataField label={t("fileDigitale.framerate")} value={fileDigitale?.framerate_data?.termine} size={6} />
                <DataField label={t("fileDigitale.codec")} value={fileDigitale?.codec_data?.termine} size={6} />
              </DataRow>
              <DataRow>
                <DataField label={t("fileDigitale.risoluzione")} value={fileDigitale?.risoluzione} size={6} />
                <DataField label={t("fileDigitale.durata")} value={fileDigitale?.durata} size={6} />
              </DataRow>
              <DataRow>
                <DataField label={t("fileDigitale.bit_depth")} value={fileDigitale?.bit_depth} size={6} />
                <DataField label={t("fileDigitale.aspect_ratio")} value={fileDigitale?.aspect_ratio} size={6} />
              </DataRow>
              <DataRow>
                <DataField
                  label={t("fileDigitale.sistema_colore")}
                  value={fileDigitale?.sistema_colore_data?.termine}
                  size={6}
                />
              </DataRow>
              <DataRow last>
                <DataField
                  label={t("fileDigitale.json_file")}
                  value={
                    fileDigitale?.json_file ? (
                      <a href={fileDigitale?.json_file} target="_blank">
                        File json
                      </a>
                    ) : null
                  }
                  size={12}
                />
              </DataRow>
            </Box>
          </Stack>
        </Grid>

        <Grid
          size={{
            xs: 12,
            xl: 12,
          }}
        >
          <Stack
            ref={ref}
            sx={{
              position: "sticky",
              top: 0,
              overflow: "auto",
              bgcolor: "grey.200",
              borderRadius: 1,
              boxShadow: 1,
              height: desiredHeight,
            }}
          >
            <SectionTitle bgcolor="grey.700" info />
            <Stack sx={{ p: 2 }} flexGrow={1}>
              <RecordInfo
                first_update={fileDigitale?.first_update}
                last_update={fileDigitale?.last_update}
                historyBag={historyBag as HistoryBag}
              />
            </Stack>
            {userCanWriteAndDelete && !isMobile && (
              <DeleteBar title={t("fileDigitale.cta_elimina")} onDelete={() => deleteModalActions.open(fileDigitale)} />
            )}
          </Stack>
        </Grid>
      </Grid>

      <DialogForm<FileDigitale>
        open={updateModalState.isOpen}
        handleClose={updateModalActions.close}
        FormComponent={FileDigitaleForm}
        hasDoubleLanguage
        title={t("fileDigitale.cta_modifica")}
        save={(values) => {
          const valuesToSave = omit(["file", "first_update", "last_update"], values)
          return update(valuesToSave as FileDigitale).then(() => {
            updateModalActions.close()
            refetch()
            showMessage(t("fileDigitale.esito_modifica_ok"))
          })
        }}
        item={fileDigitale}
        hasCancel
      />

      <DialogForm<FileDigitale>
        open={updateFileModalState.isOpen}
        handleClose={updateFileModalActions.close}
        FormComponent={FileDigitaleFileForm}
        title={t("fileDigitale.cta_modifica")}
        save={(values) => {
          const valuesToSave = omit(["file", "first_update", "last_update"], values)
          return update(valuesToSave as FileDigitale).then(() => {
            updateFileModalActions.close()
            refetch()
            showMessage(t("fileDigitale.esito_modifica_ok"))
          })
        }}
        item={fileDigitale}
        hasCancel
      />

      <DialogConfirm
        open={deleteModalState.isOpen}
        handleClose={deleteModalActions.close}
        title={t("fileDigitale.cta_elimina")}
        subtitle={
          <span>
            {t("fileDigitale.cta_subtitle_elimina")} <b>"{deleteModalState.value?.nome}"</b>&nbsp;?
          </span>
        }
        textConfirm={t("general.elimina")}
        onConfirm={() => {
          return remove(deleteModalState.value.id)
            .then(() => {
              deleteModalActions.close()
              showMessage(t("fileDigitale.esito_elimina_ok"))
              navigate("/supporti")
            })
            .catch(() => {
              deleteModalActions.close()
              showMessage(t("general.esito_elimina_errore"), "error")
            })
        }}
      />
    </>
  )
}
